import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {array, object} from 'yup';

import {
  useGetEmployeeOrgranisationalDataSalesApiQuery,
  usePatchEmployeeOrganisationalDataSalesApiMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps, yupString} from 'shared/web';

import {SALES_EMPTY_BRANCH} from '../../constants/salesEmptyBranchObject';
import {useEmployeeUrl} from '../../hooks/useEmployeeUrl';
import {EmployeeAllocation} from './components/EmployeeAllocation';
import {useBillingInformation} from './hooks/useBillingInformation';
import {EmployeeSalesOrganizationalForm} from './types/EmployeeSalesOrganizationalForm';
import {getOrganizationPlacementFormDefaultValues} from './utils/getOrganizationPlacementFormDefaultValues';

export function SalesOrganizationPlacement(props: TestIdProps) {
  const {employeeId} = useEmployeeUrl();

  const {options: billingInformationOptions} = useBillingInformation();

  const {data, isLoading, isError} = useGetEmployeeOrgranisationalDataSalesApiQuery({employeeId});
  const [patchEmployeeOrganisationalData] = usePatchEmployeeOrganisationalDataSalesApiMutation();

  const onSubmit: FormSubmitHandler<EmployeeSalesOrganizationalForm> = async (data) => {
    await patchEmployeeOrganisationalData({
      employeeId,
      body: {
        note: data?.note,
        organisationalData: {
          companyId: data.companyId ?? '',
          branches: data?.branches.map((branch) => ({
            ...branch,
            branchId: branch.branchId ?? '',
            professionId: branch?.professionId,
            authorizations: branch.authorizationIds.map((id) => ({
              authorizationProfileId: id,
            })),
          })),
        },
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const defaultValues = getOrganizationPlacementFormDefaultValues(data);

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={140}>
      <Form<EmployeeSalesOrganizationalForm>
        schema={schema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <HStack spacing={4}>
              <Box flex={1}>
                <Card title={i18n.t('page.employeeDetail.labels.organizationalClassification')}>
                  <VStack spacing={4}>
                    <FormField
                      type="choice"
                      name="companyId"
                      control={control}
                      label={i18n.t('general.labels.company')}
                      isRequired
                      options={billingInformationOptions}
                      onChange={() => formApi.setValue('branches', [SALES_EMPTY_BRANCH])}
                      isNotClearable
                      data-testid={suffixTestId('company', props)}
                    />
                    <EmployeeAllocation
                      control={control}
                      formApi={formApi}
                      data-testid={suffixTestId('allocation', props)}
                    />
                  </VStack>
                </Card>
              </Box>
              <Box flex={1}>
                <Card title={i18n.t('general.labels.notes')}>
                  <FormField
                    type="textarea"
                    name="note"
                    control={control}
                    minRows={5}
                    data-testid={suffixTestId('note', props)}
                  />
                </Card>
              </Box>
            </HStack>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={() => formApi.reset(defaultValues)}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('general.actions.saveChanges')}
                data-testid={suffixTestId('save', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const schema = object({
  companyId: yupString.required(),
  branches: array().of(
    object({
      branchId: yupString.required(),
      professionId: yupString,
      authorizationIds: array().of(yupString.required()),
    })
  ),
});
